import { useEffect, useId, useState } from "react";
import { TRIAL_DAYS } from "../../../../constants/trial";
import {
  buildLandingPriceHighlights,
  LANDING_MODULES_FALLBACK,
  PLAN_POSITIONING,
  planIncludesModule,
  resolveLandingModules,
  resolveLandingPlans,
} from "../../../../constants/planMarketing";
import platformAPI from "../../../../services/platformApi";
import { Btn } from "../SiteShell";
import InlineRegister from "../InlineRegister";
import h from "./home.module.css";

export function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

export function Title({ lines }) {
  return (
    <h2 className={h.h2}>
      {lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </h2>
  );
}

export function FaqList({ items }) {
  const [open, setOpen] = useState(0);
  const uid = useId();
  return (
    <div className={h.faq}>
      {items.map(([q, a], i) => {
        const isOpen = open === i;
        const answerId = `${uid}-faq-${i}`;
        return (
          <div key={q} className={h.faqItem}>
            <h3 className={h.faqH}>
              <button
                type="button"
                className={h.faqQ}
                aria-expanded={isOpen}
                aria-controls={answerId}
                onClick={() => setOpen(isOpen ? -1 : i)}
              >
                {q}
                <em aria-hidden>{isOpen ? "−" : "+"}</em>
              </button>
            </h3>
            <p id={answerId} className={h.faqA} hidden={!isOpen}>
              {a}
            </p>
          </div>
        );
      })}
    </div>
  );
}

export function FaqSection({ kicker, items, soft = false, aside = null }) {
  return (
    <section className={`${h.section} ${soft ? h.soft : ""}`} id="faq">
      <div className={`${h.wrap} ${h.faqRow}`}>
        <div>
          <p className={h.kicker}>{kicker}</p>
          <Title lines={["Частые", "вопросы"]} />
          <p className={h.sub}>
            Не нашли ответ? Напишите нам — поможем с запуском и переносом базы.
          </p>
          {aside}
        </div>
        <FaqList items={items} />
      </div>
    </section>
  );
}

export function RegisterSection({ kicker, title, soft = false }) {
  return (
    <section className={`${h.section} ${soft ? h.soft : ""}`}>
      <div className={h.wrap}>
        <div className={h.head}>
          <p className={h.kicker} id="register">
            {kicker}
          </p>
          <Title lines={title} />
          <p className={h.sub}>
            Без заявок и ожидания менеджера. Регистрируетесь сами — и сразу
            настраиваете расписание.
          </p>
        </div>
        <InlineRegister />
      </div>
    </section>
  );
}

export function usePublicPlans() {
  const [plans, setPlans] = useState(() => resolveLandingPlans([]));
  const [modules, setModules] = useState(LANDING_MODULES_FALLBACK);

  useEffect(() => {
    let alive = true;
    platformAPI
      .getPublicPlans()
      .then((r) => {
        if (alive) setPlans(resolveLandingPlans(r?.plans));
      })
      .catch(() => {});
    platformAPI
      .getPublicModules()
      .then((r) => {
        if (alive) setModules(resolveLandingModules(r?.modules));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return { plans, modules };
}

export function PlansCalculator({ plans, modules }) {
  const [planCode, setPlanCode] = useState(null);
  const [selectedModules, setSelectedModules] = useState(() => new Set());

  const featuredCode =
    plans.find((p) => p.badge)?.code ||
    plans.find((p) => p.code === "club")?.code;

  const selectedPlan =
    plans.find((p) => p.code === planCode) ||
    plans.find((p) => p.code === featuredCode) ||
    plans[0];
  const planMonthly = Number(selectedPlan?.monthly_price_rub) || 0;

  const moduleRows = modules
    .map((mod) => {
      const included = planIncludesModule(selectedPlan, mod.code);
      return { ...mod, included, active: included || selectedModules.has(mod.code) };
    })
    .sort((a, b) =>
      a.included !== b.included
        ? a.included
          ? -1
          : 1
        : (a.sort_order || 0) - (b.sort_order || 0),
    );

  const addonsMonthly = moduleRows.reduce(
    (sum, m) =>
      !m.included && m.pricing !== "once" && selectedModules.has(m.code)
        ? sum + (Number(m.price_rub) || 0)
        : sum,
    0,
  );

  const toggleModule = (code) =>
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  return (
    <>
      <div className={h.plans}>
        {plans.map((plan) => {
          const featured = plan.code === featuredCode;
          const selected = plan.code === selectedPlan?.code;
          return (
            <article
              key={plan.id ?? plan.code}
              className={`${h.plan} ${selected ? h.planFeatured : ""}`}
            >
              {featured ? (
                <span className={h.planBadge}>{plan.badge || "Популярный"}</span>
              ) : null}
              <h3>{plan.name}</h3>
              {PLAN_POSITIONING[plan.code] ? (
                <p className={h.planPos}>{PLAN_POSITIONING[plan.code]}</p>
              ) : null}
              <p className={h.planPrice}>
                {formatRub(plan.monthly_price_rub)} ₽<small> / мес</small>
              </p>
              <ul>
                {buildLandingPriceHighlights(plan.code).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <button
                type="button"
                className={`${h.planPick} ${selected ? h.planPickOn : ""}`}
                aria-pressed={selected}
                onClick={() => setPlanCode(plan.code)}
              >
                {selected ? "✓ Выбран для расчёта" : "Рассчитать с модулями"}
              </button>
              <Btn variant={selected ? undefined : "ghost"} className={h.planBtn}>
                {TRIAL_DAYS} дней бесплатно
              </Btn>
            </article>
          );
        })}
      </div>

      {selectedPlan ? (
        <div className={h.calc}>
          <div>
            <h3 className={h.calcTitle}>Модули</h3>
            <p className={h.calcSub}>
              Добавьте то, что нужно клубу, — сумма пересчитается сразу.
            </p>
            <div className={h.calcGrid}>
              {moduleRows.map((mod) => (
                <button
                  key={mod.code}
                  type="button"
                  className={`${h.mod} ${mod.active ? h.modOn : ""} ${
                    mod.included ? h.modIncluded : ""
                  }`}
                  onClick={() => toggleModule(mod.code)}
                  disabled={mod.included}
                  aria-pressed={mod.active}
                >
                  <span className={h.modTop}>
                    <b>{mod.name}</b>
                    <em>{mod.included ? "В тарифе" : mod.active ? "✓" : "+"}</em>
                  </span>
                  <span className={h.modDesc}>{mod.description}</span>
                  <span className={h.modPrice}>
                    {mod.included
                      ? "Уже входит"
                      : mod.pricing === "once"
                        ? `${formatRub(mod.price_rub)} ₽ разово`
                        : `+${formatRub(mod.price_rub)} ₽ / мес`}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <aside className={h.calcSum}>
            <p className={h.calcLabel}>Итого в месяц</p>
            <p className={h.calcTotal}>{formatRub(planMonthly + addonsMonthly)} ₽</p>
            <ul className={h.calcLines}>
              <li>
                <span>Тариф «{selectedPlan.name}»</span>
                <b>{formatRub(planMonthly)} ₽</b>
              </li>
              {moduleRows
                .filter((m) => !m.included && selectedModules.has(m.code))
                .map((m) => (
                  <li key={m.code}>
                    <span>{m.name}</span>
                    <b>
                      +{formatRub(m.price_rub)} ₽
                      {m.pricing === "once" ? " разово" : ""}
                    </b>
                  </li>
                ))}
            </ul>
            <Btn className={h.calcBtn}>Создать клуб</Btn>
            <p className={h.calcNote}>
              Сейчас платить не нужно — первые {TRIAL_DAYS} дней бесплатно.
            </p>
          </aside>
        </div>
      ) : null}
    </>
  );
}

export function PageHero({ pill, lines, lead, actions, visual, note }) {
  return (
    <section className={`${h.hero} ${h.heroNiche}`}>
      <div className={`${h.wrap} ${h.heroGrid} ${visual ? "" : h.heroSolo}`}>
        <div>
          <h1>
            <span className={h.pill}>{pill}</span>
            {lines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </h1>
          <p className={h.heroLead}>{lead}</p>
          {actions ? <div className={h.heroActions}>{actions}</div> : null}
          {note ? <p className={h.heroNote}>{note}</p> : null}
        </div>
        {visual ? (
          <div className={h.collage} aria-hidden>
            {visual}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function PhonesArt({ icon: Icon, screens }) {
  return (
    <div className={h.collageArt}>
      {Icon ? <Icon className={h.collageArtIcon} /> : null}
      {screens.map((src) => (
        <img key={src} src={`/landing/${src}`} alt="" />
      ))}
    </div>
  );
}
